/**
 * counsel-start-flow 가 외부(InquiryFlow.initialValues prop)에서
 * 받아야 하는 초기 컨텍스트의 TypeScript/Zod 정규 스키마.
 *
 * - 모든 최상위 키는 optional (undefined → 기본값 없음)
 * - 오브젝트 내부의 개별 필드도 optional — AI 자동기입이 없을 수 있음
 * - 이 타입은 CounselStartPage → CounselStartFlowAdapter → InquiryFlowLoader
 *   로 전달되는 initialValues 의 계약을 정의한다.
 *
 * Flow Editor의 contextSpec.fields 는 이 스키마를 반영해야 한다.
 * (counsel-start-flow.json의 contextSpec.fields 참조)
 */

import { z } from 'zod';

// ── _prefillAddress ────────────────────────────────────────────────────────────

export const PrefillAddressSchema = z.object({
  /** 도로명주소 (필수 — 없으면 _prefillAddress 자체가 undefined) */
  roadAddress: z.string(),
  /**
   * 우편번호. 백엔드 assignedDepartureLocation에는 없으므로 빈 문자열('')로 채운다.
   * AddressValue.zipCode 필드와 호환되어 road_address 질문의 initialValueFrom: '_prefillAddress'
   * 경로로 전체 객체를 전달할 수 있게 한다.
   */
  zipCode: z.string().optional(),
  /** AI 자동기입: 건물명 */
  buildingName: z.string().nullish(),
  /** 지번주소 (백엔드에서 제공하지 않으므로 optional) */
  jibunAddress: z.string().nullish(),
  /** AI 자동기입: 동 (예: "102동", "가동", "B동") */
  dong: z.string().nullish(),
  /** AI 자동기입: 호수 (floorOrUnit.type === 'Unit') */
  unit: z.string().nullish(),
  /** AI 자동기입: 층수 (floorOrUnit.type === 'Floor') */
  floor: z.string().nullish(),
});

export type PrefillAddress = z.infer<typeof PrefillAddressSchema>;

// ── CounselStartFlowContext ────────────────────────────────────────────────────

/**
 * counsel-start-flow 의 initialValues 루트 스키마.
 *
 * 각 최상위 키는 undefined-able — 전체 컨텍스트 블록이 없을 수 있음.
 */
export const CounselStartFlowContextSchema = z.object({
  /**
   * 사전 기입할 출발지 주소.
   * order.assignedDepartureLocation 에서 채워지며,
   * AI 자동기입 프로세스(RegionAssignProcess)가 buildingName/dong/floorOrUnit을
   * 추가로 채운다.
   */
  _prefillAddress: PrefillAddressSchema.optional(),
});

export type CounselStartFlowContext = z.infer<typeof CounselStartFlowContextSchema>;

// ── prefill 구성 ───────────────────────────────────────────────────────────────

/** `order.assignedDepartureLocation` 의 필요한 부분만. */
export interface AssignedDepartureLocation {
  roadAddress?: string | null;
  buildingName?: string | null;
  dong?: string | null;
  floorOrUnit?: { type?: string | null; value?: string | null } | null;
}

/**
 * 주문에 배정된 출발지 → 상담 시작 플로우의 초기 컨텍스트.
 *
 * `CounselStartPage` 안에 인라인으로 있던 계산을 꺼낸 것이다. 문서의 DataSource 가
 * derive 로 부를 수 있어야 하고, 페이지와 문서가 **같은 함수**를 써야 두 경로의 동작이
 * 갈라지지 않는다.
 *
 * 도로명주소가 없으면 undefined 를 돌려준다 — InquiryFlow 의 기본 동작(빈 accumulated)을
 * 유지하기 위해서다. 빈 객체를 주면 질문들이 빈 문자열로 채워진 것처럼 보인다.
 */
export function buildDeparturePrefill(
  loc: AssignedDepartureLocation | null | undefined
): CounselStartFlowContext | undefined {
  if (!loc?.roadAddress) return undefined;

  const unit = loc.floorOrUnit?.type === 'Unit' ? (loc.floorOrUnit.value ?? undefined) : undefined;
  const floor =
    loc.floorOrUnit?.type === 'Floor' ? (loc.floorOrUnit.value ?? undefined) : undefined;

  const raw: CounselStartFlowContext = {
    _prefillAddress: {
      roadAddress: loc.roadAddress,
      zipCode: '',
      buildingName: loc.buildingName ?? undefined,
      dong: loc.dong ?? undefined,
      unit,
      floor,
    },
  };

  const parsed = CounselStartFlowContextSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}
